import React, { useState, useEffect, useCallback } from "react";
import styles from "./ReferFriendTab.module.css";
import axios from "axios";
import moment from "moment";
import { useDispatch } from "react-redux";
import { getPlayerBalance } from "../../store/slices/user";

// Use backend base URL from environment, fallback to production API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL.replace(/\/+$/, "");
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL.replace(/\/+$/, "");

export default function ReferFriendTab() {
  const [mainTab, setMainTab] = useState("overview"); // overview | report | income | download
  const [incomeTab, setIncomeTab] = useState("income"); // income | incomeHistory
  const [inviteDetail, setInviteDetail] = useState("");
  const [withdrawDetail, setWithdrawDetail] = useState("");
  const [friendInvite, setFriendInvite] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");

  const dispatch = useDispatch();

  const resolveUserId = useCallback(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.id) return parsed.id;
      }
    } catch (_) {}
    const idFallback = localStorage.getItem("userId");
    return idFallback || null;
  }, []);

  const uid = resolveUserId();
  const referralLink = `${
    typeof window !== "undefined" ? window.location.origin : FRONTEND_URL
  }/register?friend=${uid || ""}`;

  const copy = () => {
    navigator.clipboard.writeText(referralLink).catch(() => {});
  };

  const fetchInviteSetting = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/setting/invite`);
      const list = res?.data?.data || [];
      const first = list[0] || {};
      setInviteDetail(first?.inviteDetail || "");
      setWithdrawDetail(first?.withdrawConditionDetail || "");
    } catch (e) {
      // silent
    }
  }, []);

  const fetchFriendInvite = useCallback(async () => {
    if (!uid) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/agentbalance/invitesummaryclient/${uid}`,
      );
      setFriendInvite(res?.data?.data || []);
    } catch (e) {
      // silent
    }
  }, [uid]);

  const fetchInvitedUsers = useCallback(async () => {
    if (!uid) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/agentbalance/invitedusers/${uid}`,
      );
      setInvitedUsers(res?.data?.data || []);
    } catch (e) {
      // silent
    }
  }, [uid]);

  const claimInviteBonus = useCallback(
    async (inviteId) => {
      if (!uid || !inviteId) return;
      setLoadingId(inviteId);
      setMessage("");
      try {
        const res = await axios.post(
          `${API_BASE_URL}/agentbalance/paidinvite/${uid}`,
          { data: inviteId },
        );
        if (res?.data?.success === true) {
          setMessage("รับโบนัสเชิญเพื่อนเรียบร้อยแล้ว");
          setMessageType("success");
          try {
            dispatch(getPlayerBalance({ id: uid }));
          } catch (_) {}
          await fetchFriendInvite();
        } else {
          setMessage("กรุณาเคลียร์เทิร์นก่อนรับโบนัสเชิญเพื่อน");
          setMessageType("error");
        }
      } catch (e) {
        setMessage("ไม่สามารถรับโบนัสเชิญเพื่อนได้ กรุณาติดต่อเจ้าหน้าที่");
        setMessageType("error");
      } finally {
        setLoadingId(null);
      }
    },
    [uid, fetchFriendInvite, dispatch],
  );

  useEffect(() => {
    fetchInviteSetting();
  }, [fetchInviteSetting]);

  useEffect(() => {
    fetchFriendInvite();
    fetchInvitedUsers();
  }, [fetchFriendInvite, fetchInvitedUsers]);

  const unreceived = friendInvite.filter((f) => f.inviteStatus === "UNRECEIVE");
  const claimed = friendInvite.filter((f) => f.inviteStatus !== "UNRECEIVE");
  const totalEarned = claimed.reduce(
    (sum, r) => sum + (Number(r.amount) || 0),
    0,
  );
  const pendingTotal = unreceived.reduce(
    (sum, r) => sum + (Number(r.amount) || 0),
    0,
  );

  const maskUsername = (username) => {
    const raw = String(username || "");
    if (raw.length <= 4) return "xxxx";
    return `${raw.slice(0, -4)}xxxx`;
  };

  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    copy();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.wrapper}>
      {/* ── Hero Banner ── */}
      <div className={styles.hero}>
        <div className={styles.heroGlow} />
        <div className={styles.heroEmoji}>🤝</div>
        <h3 className={styles.heroTitle}>ชวนเพื่อน ยิ่งชวน ยิ่งได้</h3>
        <p className={styles.heroDesc}>
          แชร์ลิงก์ให้เพื่อนสมัคร รับโบนัสทุกคนที่เข้าร่วม
        </p>
      </div>

      {/* ── Stats Row ── */}
      <div className={styles.statsRow}>
        <div className={styles.statBox}>
          <span className={styles.statIcon}>👥</span>
          <div className={styles.statData}>
            <span className={styles.statNum}>{invitedUsers.length}</span>
            <span className={styles.statLabel}>เพื่อนทั้งหมด</span>
          </div>
        </div>
        <div className={styles.statBox}>
          <span className={styles.statIcon}>💎</span>
          <div className={styles.statData}>
            <span className={styles.statNum}>
              {totalEarned.toLocaleString()}
            </span>
            <span className={styles.statLabel}>เครดิตที่ได้รับ</span>
          </div>
        </div>
        <div className={styles.statBox}>
          <span className={styles.statIcon}>⏳</span>
          <div className={styles.statData}>
            <span className={styles.statNum}>
              {pendingTotal.toLocaleString()}
            </span>
            <span className={styles.statLabel}>รอรับ</span>
          </div>
        </div>
      </div>

      {/* ── Referral Link ── */}
      <div className={styles.linkCard}>
        <div className={styles.linkCardHead}>
          <span className={styles.linkCardIcon}>🔗</span>
          <span className={styles.linkCardTitle}>ลิงก์แนะนำของคุณ</span>
        </div>
        <div className={styles.linkRow}>
          <input readOnly value={referralLink} className={styles.linkInput} />
          <button
            type="button"
            onClick={handleCopy}
            className={`${styles.copyBtn} ${copied ? styles.copied : ""}`}
          >
            {copied ? "✓ คัดลอกแล้ว" : "คัดลอก"}
          </button>
        </div>
        {inviteDetail && (
          <div className={styles.linkNote}>
            <span className={styles.linkNoteIcon}>💡</span>
            <span>{inviteDetail}</span>
          </div>
        )}
      </div>

      {/* ── Tabs ── */}
      <div className={styles.tabBar}>
        {[
          { id: "overview", label: "โบนัสรอรับ", count: unreceived.length },
          { id: "report", label: "ประวัติทั้งหมด", count: invitedUsers.length },
          { id: "income", label: "สรุปรายได้" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            className={`${styles.tabBtn} ${mainTab === t.id ? styles.tabActive : ""}`}
            onClick={() => setMainTab(t.id)}
          >
            {t.label}
            {t.count !== undefined && t.count > 0 && (
              <span className={styles.tabBadge}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab: โบนัสรอรับ ── */}
      {mainTab === "overview" && (
        <div className={styles.tabContent}>
          {unreceived.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyEmoji}>📭</div>
              <p className={styles.emptyTitle}>ยังไม่มีโบนัสรอรับ</p>
              <p className={styles.emptyDesc}>
                เริ่มแชร์ลิงก์ด้านบนให้เพื่อนสมัคร เพื่อรับโบนัสทันที
              </p>
            </div>
          ) : (
            <div className={styles.bonusList}>
              {unreceived.map((row) => (
                <div key={row.id} className={styles.bonusItem}>
                  <div className={styles.bonusLeft}>
                    <div className={styles.bonusAvatar}>
                      {(row?.username || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className={styles.bonusMeta}>
                      <span className={styles.bonusName}>{row?.username}</span>
                      <span className={styles.bonusTime}>
                        {moment(row?.created_at)
                          .add(543, "year")
                          .format("DD/MM/YY HH:mm")}
                      </span>
                    </div>
                  </div>
                  <div className={styles.bonusRight}>
                    <span className={styles.bonusCredit}>+{row?.amount}</span>
                    <button
                      type="button"
                      className={styles.claimBtn}
                      disabled={loadingId === row.id}
                      onClick={() => claimInviteBonus(row.id)}
                    >
                      {loadingId === row.id ? "⏳" : "รับ"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {withdrawDetail && (
            <div className={styles.conditionNote}>
              <span>📌</span>
              <span>เงื่อนไข : {withdrawDetail}</span>
            </div>
          )}

          {message && (
            <div className={`${styles.toast} ${styles[messageType]}`}>
              {messageType === "success" ? "✅" : "⚠️"} {message}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: ประวัติทั้งหมด ── */}
      {mainTab === "report" && (
        <div className={styles.tabContent}>
          {invitedUsers.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyEmoji}>📋</div>
              <p className={styles.emptyTitle}>ยังไม่มีรายการ</p>
              <p className={styles.emptyDesc}>
                เมื่อเพื่อนสมัครผ่านลิงก์ของคุณ จะแสดงรายการที่นี่
              </p>
            </div>
          ) : (
            <div className={styles.historyList}>
              <div className={styles.historyItem}>
                <div className={styles.historyAvatar}>
                  <span className={styles.historyName}>#</span>
                </div>
                <div className={styles.historyMeta}>
                  <span className={styles.historyName}>ชื่อผู้ใช้</span>
                  <span className={styles.historyDate}>ชื่อบัญชี</span>
                </div>
                <div className={styles.historyRight}>
                  <span className={styles.historyAmount}>วันที่สมัคร</span>
                </div>
              </div>
              {invitedUsers.map((row, idx) => (
                <div key={idx} className={styles.historyItem}>
                  <div className={styles.historyAvatar}>
                    {idx + 1}
                  </div>
                  <div className={styles.historyMeta}>
                    <span className={styles.historyName}>
                      {maskUsername(row.username)}
                    </span>
                    <span className={styles.historyDate}>
                      {row.book_name || "-"}
                    </span>
                  </div>
                  <div className={styles.historyRight}>
                    <span className={styles.historyAmount}>
                      {moment(row.created_at).add(543, "year").format("DD/MM/YY HH:mm")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: สรุปรายได้ ── */}
      {mainTab === "income" && (
        <div className={styles.tabContent}>
          <div className={styles.incomeSub}>
            <button
              type="button"
              className={`${styles.subBtn} ${
                incomeTab === "income" ? styles.subActive : ""
              }`}
              onClick={() => setIncomeTab("income")}
            >
              ยอดรายเดือน
            </button>
            <button
              type="button"
              className={`${styles.subBtn} ${
                incomeTab === "history" ? styles.subActive : ""
              }`}
              onClick={() => setIncomeTab("history")}
            >
              ถอนเงิน
            </button>
          </div>

          {incomeTab === "income" && (
            <>
              <div className={styles.incomeTable}>
                <div className={styles.incomeHead}>
                  <span>วันเวลา</span>
                  <span>ช่องทาง</span>
                  <span className={styles.right}>จำนวน</span>
                </div>
                <div className={styles.emptyState}>
                  <p className={styles.emptyTitle}>ยังไม่มีรายการ</p>
                </div>
              </div>
            </>
          )}

          {incomeTab === "history" && (
            <div className={styles.withdrawCard}>
              <div className={styles.withdrawLabel}>ยอดคงเหลือ</div>
              <div className={styles.withdrawAmount}>0.00 THB</div>
              <button type="button" className={styles.withdrawBtn} disabled>
                ถอนเข้ากระเป๋า
              </button>
              <p className={styles.withdrawNote}>
                ระบบถอนยังไม่เปิดให้บริการในขณะนี้
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
